import { BlurEvent, Button, ButtonVariant, ClickEvent, Component, DataDisplayEvent, DataHeader, DataSource, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownSelectionEvent, LookupModelSelectionEvent, Panel, ScreenStack, Snackbar, Textbox, Toast } from "@mcleod/components";
import { AutogenLayoutDistanceRateTable } from "./autogen/AutogenLayoutDistanceRateTable";
import { HorizontalAlignment, ModelRow, ModelSearchResult, StringUtil, VerticalAlignment } from "@mcleod/core";
import { DistanceRateTableDimensionPrompt } from "./DistanceRateTableDimensionPrompt";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { ImageName } from "@mcleod/images";
import { ModelMileageRate } from "./models/ModelMileageRate";
import { CopyDistanceRateTable } from "./CopyDistanceRateTable";
import { AdjustDistanceRateTable } from "./AdjustDistanceRateTable";

const DISTANCE = "D";
const CWT = "C";
const TON = "T";

const viewTableURL = "/lme/powerbroker/DistanceRateTable?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "rate_header",
    screenName: "Distance Rate Table",
    navigateToUrl: viewTableURL
};

const moveButtonProps = {
    preventCollapse: true,
    width: 25,
    height: 25,
    color: "primary",
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: 2,
    marginRight: 2,
    rowBreak: false
}

export class DistanceRateTable extends AutogenLayoutDistanceRateTable {

    private _showControls: boolean = false;

    onLoad(): void | Promise<void> {
        this.sourceMileageRate.addDisplayListener((event: DataDisplayEvent) =>
            this.buttonAdjustRates.enabled = this.sourceMileageRate.rowCount > 0
        );

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            const data: ModelSearchResult = await new ModelMileageRate().search({ "table_id": this.sourceRateHeader.activeRow.get("id") });
            if (data.modelRows.length > 0) {
                await Promise.all(data.modelRows.map(row => row.delete()));
                this.sourceMileageRate.data.forEach(row => row._appending = true);
            }
            return true;
        }

        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
    }

    textboxMethodOnDataDisplay(event: DataDisplayEvent): void {
        const method = event.rowData?.getString("method", null);
        const currency = event.rowData?.getString("currency", null);
        this.syncLayoutWithMethod(method);
        this.syncLayoutWithMethodAndCurrency(method, currency);
    }

    textboxMethodAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const method = event.selection?.value;
        const currency = this.activeRow?.getString("currency", "USD");
        this.syncLayoutWithMethod(method);
        this.syncLayoutWithMethodAndCurrency(method, currency);
    }

    textboxCurrencyOnDataDisplay(event: DataDisplayEvent): void {
        const method = event.rowData?.getString("method", null);
        const currency = event.rowData?.getString("currency", null);
        this.syncLayoutWithMethodAndCurrency(method, currency);
    }

    textboxCurrencyOnLookupModelSelection(event: LookupModelSelectionEvent): void {
        const currency = event.selection?.getString("currency_type_id", "USD");
        const method = this.activeRow?.getString("method", null);
        this.syncLayoutWithMethodAndCurrency(method, currency);
    }

    syncLayoutWithMethodAndCurrency(method: any, currency: any) {
        let gridTitle = "Rates";
        if (currency) {
            gridTitle += ` in ${currency}`;
        }
        switch (method) {
            case DISTANCE:
                gridTitle += ` per Mile`;
                break;
            case CWT:
                gridTitle += ` per 100 Pounds`;
                break;
            case TON:
                gridTitle += ` per 2000 Pounds`;
                break;
        }
        this.labelRates.text = gridTitle;
    }

    syncLayoutWithMethod(method: string): void {
        const isWeightBased = method === CWT || method === TON;
        const isDistanceBased = method === DISTANCE;
        this.textboxMinimumWeight.visible = isWeightBased;
        if (isDistanceBased && this.activeRow?.getNumber("minimum_weight", 0) > 0) {
            this.activeRow.set("minimum_weight", null);
        }
    }

    sourceRateHeaderAfterExecution(event: DataSourceExecutionEvent): void {
        this.sourceMileageRate.search().then((data: ModelSearchResult) => {
            if (data.modelRows.length > 0) {
                this.buildRateTable(data.modelRows);
            }
        });
    }

    sourceRateHeaderAfterModeChange(event: DataSourceModeChangeEvent) {
        this.buttonCopy.visible = event.newMode === DataSourceMode.UPDATE;
        this.buttonAdjustRates.visible = event.newMode === DataSourceMode.UPDATE;
        this.buttonCreateTable.visible = event.newMode !== DataSourceMode.SEARCH;
        if (event.newMode === DataSourceMode.UPDATE) {
            this.mainDataSource.addHasChangedComponent(this.buttonCopy);
            if (this.activeRow != null) {
                this.buttonCopy.doOnDataSourceChanged = (dataSource: DataSource, changesPresent: boolean) => {
                    this.buttonCopy.enabled = !changesPresent;
                }
            }
        }
    }

    buttonCreateTableOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        DistanceRateTableDimensionPrompt.showModal((layout) => this.createNewRateTable(layout))
            .finally(() => button.busy = false);
    }

    async createNewRateTable(layout: DistanceRateTableDimensionPrompt) {
        this.panelRates.busy = true;
        const progress = Snackbar.showDownloadSnackbar(
            "Action in Progress",
            "Rendering Rate Table...",
            { persist: true, targetPanel: ScreenStack.getSnackbarTarget() }
        );
        try {
            const numColumns = layout.textboxWeight.text ? Number(layout.textboxWeight.text) : 2;
            const numRows = layout.textboxDistance.text ? Number(layout.textboxDistance.text) : 1;
            const maxWeight = layout.textboxMaxWeight.text ? Number(layout.textboxMaxWeight.text) : 0;
            const maxDistance = layout.textboxMaxDistance.text ? Number(layout.textboxMaxDistance.text) : 0;
            const weightInc = layout.textboxWeightInc.text ? Number(layout.textboxWeightInc.text) : 0;
            const distanceInc = layout.textboxDistanceInc.text ? Number(layout.textboxDistanceInc.text) : 0;

            const newModelRows = await this.buildRateTableData(numColumns, numRows, maxWeight, maxDistance, weightInc, distanceInc);

            this.sourceMileageRate.setRowsAndMode(this.sourceRateHeader.mode, newModelRows);
            this.sourceRateHeader.activeRow.clearLinkedModels();
            this.sourceRateHeader.activeRow.addLinkedModel({
                model: this.sourceMileageRate.url,
                rows: this.sourceMileageRate.data
            });
            this.sourceRateHeader.notifyHasChangedComponents(true);
            await this.buildRateTable(newModelRows);
        } finally {
            progress.dismiss();
            this.panelRates.busy = false;
        }
    }

    async buttonAddColumnOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        this.panelRates.busy = true;
        await this.addCells("column_number");
        button.busy = false;
        this.panelRates.busy = false;
    }

    async buttonAddRowOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        this.panelRates.busy = true;
        await this.addCells("row_number");
        button.busy = false;
        this.panelRates.busy = false;
    }

    private headerCellOnBlur(event: BlurEvent) {
        const tb = event.target as Textbox;
        if (StringUtil.isEmptyString(tb.text)
            || "<" === (tb.text)) {
            tb.text = "<0";
        } else if (!tb.text.startsWith("<")) {
            tb.text = "<" + tb.text;
        }
        const text = tb.text.substring(1);
        if (isNaN(Number(text))) {
            tb.text = "<0";
        }
        tb.boundRow.set("rate_value", tb.text);
    }

    private cellOnBlur(event: BlurEvent) {
        const tb = event.target as Textbox;
        const formatted = (!StringUtil.isEmptyString(tb.text) && isNaN(Number(tb.text))) ? "0.00" : Number(tb.text).toFixed(2);
        tb.text = formatted;
        tb.boundRow.set("rate_value", formatted);
    }

    async buildRateTable(data: ModelRow[]) {
        this.panelRates.removeAll();
        this.panelRateTable.visible = true;
        const lastColumnIdx = data.map(row => row.getNumber("column_number", 0)).reduce((max, current) => Math.max(max, current), 0);
        const lastRowIdx = data.map(row => row.getNumber("row_number", 0)).reduce((max, current) => Math.max(max, current), 0);

        const tbwidth = 150;
        const components: Component[] = [];
        this.buildColumnControls(components, lastColumnIdx, tbwidth);

        data.forEach((row, i) => this.buildCell(row, data, i, lastColumnIdx, lastRowIdx, tbwidth, components));
        this.panelRates.add(...components);
    }


    buildColumnControls(components: Component[], lastColumnIdx: number, tbwidth: number) {
        for (let i = 0; i <= lastColumnIdx; i++) {
            const panel = new Panel({
                id: `columnControls${i}`,
                align: HorizontalAlignment.CENTER,
                width: tbwidth,
                minWidth: tbwidth,
                marginTop: 10,
                preventCollapse: true,
                visible: this.showControls,
                rowBreak: i === lastColumnIdx,
            });
            if (i > 1) {
                panel.add(new Button({ ...moveButtonProps, imageName: ImageName.chevron, imageRotation: 90, visible: i > 2, onClick: (event: ClickEvent) => this.moveCells("column_number", i, -1) }));
                panel.add(new Button({ ...moveButtonProps, imageName: ImageName.delete, visible: i > 2, onClick: (event: ClickEvent) => this.deleteCells("column_number", i) }));
                panel.add(new Button({ ...moveButtonProps, imageName: ImageName.chevron, imageRotation: 270, visible: i < lastColumnIdx, onClick: (event: ClickEvent) => this.moveCells("column_number", i, 1) }));
            }
            components.push(panel);
        }
    }

    buildCell(row: ModelRow<any>, allData: ModelRow[], i: number, lastColumnIdx: number, lastRowIdx: number, tbwidth: number, components: Component[]): void {
        const columnNumber = row.getNumber("column_number", 0);
        const rowNumber = row.getNumber("row_number", 0);
        const headerCell = columnNumber === 0 || rowNumber === 0;
        const tb = new Textbox({
            id: `tb_${rowNumber}_${columnNumber}`,
            rowBreak: false,
            preventCollapse: true,
            align: HorizontalAlignment.RIGHT,
            visible: !(columnNumber === 0 && rowNumber === 0),
            printable: headerCell,
            dataSource: this.sourceMileageRate,
            field: "rate_value",
            boundRow: row,
            minWidth: tbwidth,
            width: tbwidth
        });
        tb.displayData(row, allData, i);
        tb.addBlurListener(headerCell ? this.headerCellOnBlur : this.cellOnBlur);
        components.push((headerCell) ? this.buildHeaderCell(tb, tbwidth, rowNumber, columnNumber, lastColumnIdx, lastRowIdx) : tb);
        if (columnNumber === lastColumnIdx) {
            const panel = new Panel({
                id: `rowControls${rowNumber}`,
                align: HorizontalAlignment.CENTER,
                width: tbwidth,
                minWidth: tbwidth,
                rowBreak: true,
                preventCollapse: true,
                visible: this.showControls
            });
            if (rowNumber > 0) {
                panel.add(new Button({ ...moveButtonProps, imageName: ImageName.chevron, imageRotation: 180, visible: rowNumber > 1, onClick: (event: ClickEvent) => this.moveCells("row_number", rowNumber, -1) }));
                panel.add(new Button({ ...moveButtonProps, imageName: ImageName.delete, visible: rowNumber > 1, onClick: (event: ClickEvent) => this.deleteCells("row_number", rowNumber) }));
                panel.add(new Button({ ...moveButtonProps, imageName: ImageName.chevron, visible: rowNumber < lastRowIdx, onClick: (event: ClickEvent) => this.moveCells("row_number", rowNumber, 1) }));
            }
            components.push(panel);
        }
    }

    buildHeaderCell(tb: Textbox, width: number, rowNumber: number, columnNumber: number, lastColumnIdx: number, lastRowIdx: number): Panel {
        const panel = new Panel({
            align: HorizontalAlignment.CENTER,
            verticalAlign: VerticalAlignment.BOTTOM,
            width: width,
            minWidth: width,
            rowBreak: columnNumber === lastColumnIdx,
        });
        tb.setProps({
            width: null,
            minWidth: null,
            fontSize: "xlarge",
            fontBold: true,
        });
        panel.add(tb);
        if (rowNumber === 0 && columnNumber === 0) {
            panel.add(new Button({
                ...moveButtonProps,
                marginBottom: 0,
                imageName: ImageName.settings,
                imageHeight: 25,
                imageWidth: 25,
                variant: ButtonVariant.round,
                visible: (lastColumnIdx > 2 || lastRowIdx > 1),
                onClick: (event: ClickEvent) => this.showControls = !this.showControls,
            }));
        }
        if (!(rowNumber === 0 && columnNumber <= 1)) {//printable toggle not needed for first two cells
            const togglePrintable = (print) => {
                tb.width = print ? null : width;
                tb.printable = print;
            };
            panel.addClickListener(event => togglePrintable(false));
            tb.addBlurListener(event => togglePrintable(true));
        }
        return panel;
    }

    async addCells(fieldName: "column_number" | "row_number") {
        const lastColumnIdx = this.sourceMileageRate.data.map(row => row.getNumber("column_number", 0)).reduce((max, current) => Math.max(max, current), 0);
        const lastRowIdx = this.sourceMileageRate.data.map(row => row.getNumber("row_number", 0)).reduce((max, current) => Math.max(max, current), 0);
        const newRows = [];
        if (fieldName === "column_number") {
            for (let i = 0; i <= lastRowIdx; i++) {
                const modelRow = await this.sourceMileageRate.createBlankRow();
                modelRow.setValues({
                    "table_id": this.sourceRateHeader.activeRow.get("id"),
                    "row_number": i,
                    "column_number": lastColumnIdx + 1,
                    "rate_value": i > 0 ? "0.00" : "<0"
                });
                newRows.push(modelRow);
            }
        } else if (fieldName === "row_number") {
            for (let i = 0; i <= lastColumnIdx; i++) {
                const modelRow = await this.sourceMileageRate.createBlankRow();
                modelRow.setValues({
                    "table_id": this.sourceRateHeader.activeRow.get("id"),
                    "row_number": lastRowIdx + 1,
                    "column_number": i,
                    "rate_value": i > 0 ? "0.00" : "<0"
                });
                newRows.push(modelRow);
            }
        }
        this.sourceMileageRate.setRowsAndMode(this.sourceRateHeader.mode, this.sourceMileageRate.data.concat(newRows));
        this.sortCells();
        this.sourceMileageRate.notifyHasChangedComponents(true);

        return this.buildRateTable(this.sourceMileageRate.data);
    }

    async deleteCells(fieldName: "column_number" | "row_number", source: number) {
        this.panelRates.busy = true;
        const remaining = this.sourceMileageRate.data.filter(row => row.getNumber(fieldName, 0) !== source);
        remaining.forEach(row => {
            if (row.getNumber(fieldName, 0) > source) {
                row.set(fieldName, row.getNumber(fieldName, 0) - 1);
            }
        });
        this.sourceMileageRate.setRowsAndMode(this.sourceRateHeader.mode, remaining);
        this.sourceMileageRate.notifyHasChangedComponents(true);
        this.sortCells();

        await this.buildRateTable(this.sourceMileageRate.data);
        this.panelRates.busy = false;
    }

    async moveCells(fieldName: "column_number" | "row_number", source: number, increment: number) {
        this.panelRates.busy = true;
        const sourceRows = this.sourceMileageRate.data.filter(row => row.getNumber(fieldName, 0) === source);
        const targetRows = this.sourceMileageRate.data.filter(row => row.getNumber(fieldName, 0) === (source + increment));
        sourceRows.forEach(row => row.set(fieldName, source + increment));
        targetRows.forEach(row => row.set(fieldName, source));
        this.sortCells();
        this.sourceMileageRate.notifyHasChangedComponents(true);
        await this.buildRateTable(this.sourceMileageRate.data);
        this.panelRates.busy = false;
    }

    sortCells() {
        this.sourceMileageRate.data.sort((a, b) => {
            const rowSort = a.getNumber("row_number", 0) - b.getNumber("row_number", 0);
            if (rowSort == 0) {
                return a.getNumber("column_number", 0) - b.getNumber("column_number", 0);
            } else {
                return rowSort;
            }
        });
    }

    async buildRateTableData(numColumns: number, numRows: number, maxWeight: number, maxDistance: number, weightInc: number, distanceInc: number) {
        const newRows: ModelRow[][] = [];
        let i = 0;
        let j = 0;
        for (i = 0; i <= numRows; i++) {
            const newRow: ModelRow[] = [];
            for (j = 0; j <= numColumns; j++) {
                const modelRow = await this.sourceMileageRate.createBlankRow();
                modelRow.setValues({
                    "table_id": this.sourceRateHeader.activeRow.get("id"),
                    "row_number": i,
                    "column_number": j,
                    "rate_value": "0.00"
                });
                newRow.push(modelRow);
            }
            newRows.push(newRow);
        }

        newRows[0][1].set("rate_value", "Min.chg.");

        i = 0;
        for (j = numColumns; j > 1; j--) {
            const topRow = newRows[0][j];
            const val = maxWeight - (i * weightInc);
            topRow.set("rate_value", `<${val >= 0 ? val : 0}`);
            i++;
        }

        i = 0;
        for (j = numRows; j > 0; j--) {
            const leftColumn = newRows[j][0];
            const val = maxDistance - (i * distanceInc);
            leftColumn.set("rate_value", `<${val >= 0 ? val : 0}`);
            i++;
        }
        return newRows.flat();
    }

    get showControls(): boolean {
        return this._showControls;
    }

    set showControls(value: boolean) {
        if (this._showControls !== value) {
            this._showControls = value;
            this.buildRateTable(this.sourceMileageRate.data);
        }
    }

    buttonCopyOnClick(event: ClickEvent) {
        const sourceId = this.sourceRateHeader.activeRow?.getString("id");
        if (sourceId) {
            return CopyDistanceRateTable.showModal(sourceId);
        }
    }

    buttonAdjustRatesOnClick(event: ClickEvent) {
        return AdjustDistanceRateTable.showModal(this.sourceRateHeader.activeRow?.getString("id"))
            .then((details: { type: "I" | "D", percent: number, adjustMinimum: boolean }) => {
                if (details) {
                    const progress = Snackbar.showDownloadSnackbar(
                        "Action in Progress", "Adjusting Rates...",
                        { persist: true, targetPanel: ScreenStack.getSnackbarTarget() }
                    );
                    const targetData = this.sourceMileageRate.data.filter(row => {
                        const rowNumber = row.getNumber("row_number", 0);
                        const columnNumber = row.getNumber("column_number", 0);
                        return rowNumber > 0 && columnNumber > 0
                            && (details.adjustMinimum || (columnNumber > 1));
                    });
                    targetData.forEach(row => {
                        const rateValue = Number(row.getString("rate_value", "0"));
                        if (rateValue > 0) {
                            const percent = details.percent / 100;
                            const newValue = details.type === "I" ? rateValue * (1 + percent) : rateValue * (1 - percent);
                            row.set("rate_value", newValue.toFixed(2));
                        }
                    });
                    return this.buildRateTable(this.sourceMileageRate.data).finally(() => {
                        progress.dismiss();
                        Toast.showSuccessToast("Successfully Adjusted Table Rates");
                    });
                }
            });

    }
}
